import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContext } from './dialog-context';

const VARIANTS = {
  default: { accent: '#a346d5', bg: 'rgba(163,70,213,0.1)',  border: 'rgba(163,70,213,0.25)' },
  danger:  { accent: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.25)'  },
  success: { accent: '#4caf89', bg: 'rgba(76,175,137,0.1)',  border: 'rgba(76,175,137,0.25)' },
  warning: { accent: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)' },
  info:    { accent: '#009efa', bg: 'rgba(0,158,250,0.1)',   border: 'rgba(0,158,250,0.25)'  },
};

function IconDefault({ color }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2"/>
      <path d="M12 8v4m0 4h.01" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function IconDanger({ color }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
      <path d="M12 9v4m0 4h.01" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function IconSuccess({ color }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2"/>
      <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconWarning({ color }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2"/>
      <path d="M12 8v4m0 4h.01" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function IconInfo({ color }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2"/>
      <path d="M12 16v-4m0-4h.01" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

const ICON_MAP = { default: IconDefault, danger: IconDanger, success: IconSuccess, warning: IconWarning, info: IconInfo };

function DialogModal({ dialog, onClose }) {
  const { id, type, message, title, variant = 'default', confirmLabel, cancelLabel, okLabel } = dialog;
  const [inputValue, setInputValue] = useState(dialog.defaultValue || '');
  const { t } = useTranslation('common');

  const v = VARIANTS[variant] || VARIANTS.default;
  const IconComp = ICON_MAP[variant] || ICON_MAP.default;

  const handleConfirm = () => onClose(id, type === 'prompt' ? inputValue : true);
  const handleCancel  = () => onClose(id, type === 'prompt' ? null : false);
  const handleOk      = () => onClose(id, undefined);

  // All fallback labels resolved through t() — no hardcoded strings
  const confirmText = confirmLabel || (type === 'confirm'
    ? t('dialog_confirm')
    : t('dialog_save'));

  const cancelText = cancelLabel || t('cancel');
  const okText     = okLabel     || t('ok');

  const displayTitle = title || (
    type === 'confirm'
      ? (variant === 'danger' ? t('dialog_title_confirm_danger') : t('dialog_title_confirm'))
      : type === 'alert'
      ? (variant === 'success'
          ? t('dialog_title_success')
          : variant === 'warning'
          ? t('dialog_title_warning')
          : t('dialog_title_info'))
      : t('dialog_title_prompt')
  );

  const confirmBg =
    variant === 'danger'  ? 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' :
    variant === 'success' ? 'linear-gradient(135deg, #a8edbc 0%, #4caf89 100%)' :
    variant === 'warning' ? 'linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)' :
    variant === 'info'    ? 'linear-gradient(135deg, #82d8e8 0%, #009efa 100%)' :
    'linear-gradient(135deg, #d1b7f3 0%, #a346d5 100%)';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        background: 'rgba(26,58,66,0.55)',
        backdropFilter: 'blur(4px)',
        animation: 'bkDialogFadeIn 0.18s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { type === 'alert' ? handleOk() : handleCancel(); } }}
    >
      <style>{`
        @keyframes bkDialogFadeIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes bkDialogSlideIn { from { opacity:0; transform:translateY(14px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
      `}</style>

      <div
        style={{
          background: 'rgba(255,255,255,0.98)',
          borderRadius: 24,
          padding: '28px 28px 24px',
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
          animation: 'bkDialogSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)',
          fontFamily: "'DM Sans', 'Inter', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: v.bg, border: `1.5px solid ${v.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconComp color={v.accent} />
          </div>
          <div style={{ paddingTop: 2, flex: 1 }}>
            <p style={{ fontWeight: 800, fontSize: 15, color: '#1a3a42', letterSpacing: '-0.01em', margin: '0 0 5px' }}>
              {displayTitle}
            </p>
            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, fontWeight: 500, margin: 0 }}>
              {message}
            </p>
          </div>
        </div>

        {type === 'prompt' && (
          <input
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); if (e.key === 'Escape') handleCancel(); }}
            placeholder={dialog.placeholder || ''}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 12,
              fontSize: 14, fontWeight: 500, color: '#1a3a42',
              border: `1.5px solid ${v.border}`, background: '#f9f9f9',
              outline: 'none', marginBottom: 16, boxSizing: 'border-box',
            }}
            onFocus={(e) => { e.currentTarget.style.border = `1.5px solid ${v.accent}`; e.currentTarget.style.background = '#fff'; }}
            onBlur={(e)  => { e.currentTarget.style.border = `1.5px solid ${v.border}`;  e.currentTarget.style.background = '#f9f9f9'; }}
          />
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          {type === 'alert' ? (
            <button
              onClick={handleOk}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 14, border: 'none',
                fontWeight: 800, fontSize: 13, color: '#fff', cursor: 'pointer',
                background: confirmBg, boxShadow: `0 6px 18px ${v.accent}44`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              {okText}
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 14, cursor: 'pointer',
                  fontWeight: 700, fontSize: 13, color: '#4a7a88',
                  background: 'rgba(130,178,192,0.12)',
                  border: '1.5px solid rgba(130,178,192,0.2)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(130,178,192,0.22)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(130,178,192,0.12)')}
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 14, border: 'none', cursor: 'pointer',
                  fontWeight: 800, fontSize: 13, color: '#fff',
                  background: confirmBg, boxShadow: `0 6px 18px ${v.accent}44`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                {confirmText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);

  const open = useCallback((config) => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      setDialogs((prev) => [...prev, { id, ...config, resolve }]);
    });
  }, []);

  const close = useCallback((id, value) => {
    setDialogs((prev) => {
      const dialog = prev.find((d) => d.id === id);
      if (dialog) dialog.resolve(value);
      return prev.filter((d) => d.id !== id);
    });
  }, []);

  useEffect(() => {
    return () => {
      setDialogs((prev) => {
        prev.forEach((d) => d.resolve(undefined));
        return [];
      });
    };
  }, []);

  const alert   = useCallback((message, options = {}) => open({ type: 'alert',   message, ...options }), [open]);
  const confirm = useCallback((message, options = {}) => open({ type: 'confirm', message, ...options }), [open]);
  const prompt  = useCallback((message, options = {}) => open({ type: 'prompt',  message, ...options }), [open]);

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {dialogs.map((d) => (
        <DialogModal key={d.id} dialog={d} onClose={close} />
      ))}
    </DialogContext.Provider>
  );
}